import React, { useState, useMemo } from 'react';
import { Search, CheckCircle, Copy, DollarSign, Filter, SortAsc, SortDesc } from 'lucide-react';
import type { GoDaddySearchToolResult, ToolResultRendererProps, DomainSuggestion } from '../../../types/toolResults';
import CustomSelect from '../../common/CustomSelect';
import Tooltip from '../../common/Tooltip';

type SortField = 'domain' | 'price' | 'tld';
type SortDirection = 'asc' | 'desc';
type FilterTLD = string | 'all';

/**
 * Renderer for GoDaddy domain search results.
 *
 * Displays domain suggestions with:
 * - TLD badges and filtering
 * - Sorting by domain name, price, or TLD
 * - Availability indicators
 * - Copy-to-clipboard functionality
 */
const DomainSearchRenderer: React.FC<ToolResultRendererProps<GoDaddySearchToolResult>> = ({
  result,
  onCopy,
  className = ''
}) => {
  const { suggestions, query, total_found } = result;

  const [sortField, setSortField] = useState<SortField>('domain');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [filterTLD, setFilterTLD] = useState<FilterTLD>('all');

  const handleCopy = (text: string) => {
    if (onCopy) {
      onCopy(text);
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  const formatPrice = (priceInCents?: number, currency: string = 'USD'): string => {
    if (!priceInCents) return 'N/A';
    const price = priceInCents / 100;
    return `$${price.toFixed(2)}`;
  };

  // Extract TLD from domain
  const getTLD = (domain: string): string => {
    const parts = domain.split('.');
    return parts.length > 1 ? `.${parts[parts.length - 1]}` : '';
  };

  // Get unique TLDs for filtering
  const uniqueTLDs = useMemo(() => {
    const tlds = new Set(suggestions.map(s => getTLD(s.domain)));
    return Array.from(tlds).sort();
  }, [suggestions]);

  // Filter and sort suggestions
  const filteredAndSorted = useMemo(() => {
    let filtered = [...suggestions];

    // Apply TLD filter
    if (filterTLD !== 'all') {
      filtered = filtered.filter(s => getTLD(s.domain) === filterTLD);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'domain':
          comparison = a.domain.localeCompare(b.domain);
          break;
        case 'price':
          const priceA = a.price || Infinity;
          const priceB = b.price || Infinity;
          comparison = priceA - priceB;
          break;
        case 'tld':
          comparison = getTLD(a.domain).localeCompare(getTLD(b.domain));
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [suggestions, filterTLD, sortField, sortDirection]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (suggestions.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <p className="text-gray-500 dark:text-gray-400">No domain suggestions found for "{query}"</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header Card */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-primary-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
              <Search className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Domain Search Results</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Query: <span className="font-medium">"{query}"</span> • {total_found} results
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        <div className="flex items-center space-x-4">
          {/* TLD Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by TLD:</label>
            <CustomSelect
              value={filterTLD}
              onChange={(value) => setFilterTLD(value as FilterTLD)}
              options={[
                { value: 'all', label: `All TLDs (${suggestions.length})` },
                ...uniqueTLDs.map(tld => {
                  const count = suggestions.filter(s => getTLD(s.domain) === tld).length;
                  return { value: tld, label: `${tld} (${count})` };
                }),
              ]}
            />
          </div>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredAndSorted.length} of {total_found} results
        </div>
      </div>

      {/* Results Grid */}
      <div className="grid grid-cols-1 gap-3">
        {/* Table Header */}
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2">
          <div className="grid grid-cols-12 gap-4 items-center">
            {/* Domain Column Header */}
            <button
              onClick={() => toggleSort('domain')}
              className="col-span-6 flex items-center space-x-1 text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider hover:text-gray-900 dark:text-warm-white transition-colors"
            >
              <span>Domain</span>
              {sortField === 'domain' && (
                sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />
              )}
            </button>

            {/* TLD Column Header */}
            <button
              onClick={() => toggleSort('tld')}
              className="col-span-2 flex items-center space-x-1 text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider hover:text-gray-900 dark:text-warm-white transition-colors"
            >
              <span>TLD</span>
              {sortField === 'tld' && (
                sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />
              )}
            </button>

            {/* Price Column Header */}
            <button
              onClick={() => toggleSort('price')}
              className="col-span-2 flex items-center space-x-1 text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider hover:text-gray-900 dark:text-warm-white transition-colors"
            >
              <span>Price</span>
              {sortField === 'price' && (
                sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />
              )}
            </button>

            {/* Actions Column Header */}
            <div className="col-span-2 text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider text-right">
              Actions
            </div>
          </div>
        </div>

        {/* Results */}
        {filteredAndSorted.map((suggestion, index) => {
          const tld = getTLD(suggestion.domain);
          const baseDomain = suggestion.domain.replace(tld, '');

          return (
            <div
              key={`${suggestion.domain}-${index}`}
              className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 hover:border-blue-300 hover:shadow-sm transition-all"
            >
              <div className="grid grid-cols-12 gap-4 items-center">
                {/* Domain Column */}
                <div className="col-span-6 flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-warm-white truncate">
                      {baseDomain}<span className="text-primary-600">{tld}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Available for registration</div>
                  </div>
                </div>

                {/* TLD Badge Column */}
                <div className="col-span-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {tld}
                  </span>
                </div>

                {/* Price Column */}
                <div className="col-span-2">
                  {suggestion.price ? (
                    <div className="flex items-center space-x-1">
                      <DollarSign className="w-3 h-3 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-warm-white">
                        {formatPrice(suggestion.price, suggestion.currency)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </div>

                {/* Actions Column */}
                <div className="col-span-2 text-right">
                  <Tooltip content="Copy domain name">
                    <button
                      onClick={() => handleCopy(suggestion.domain)}
                      className="inline-flex items-center space-x-1 text-xs text-primary-600 hover:text-primary-800 transition-colors"
                    >
                      <Copy className="w-3 h-3" />
                      <span>Copy</span>
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Copy All Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            const domains = filteredAndSorted.map(s => s.domain).join('\n');
            handleCopy(domains);
          }}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/40 rounded-md transition-colors border border-primary-300 dark:border-blue-800"
        >
          <Copy className="w-4 h-4" />
          <span>Copy All Filtered Domains ({filteredAndSorted.length})</span>
        </button>
      </div>
    </div>
  );
};

export default DomainSearchRenderer;
